import React from 'react';
import { RefreshCw, Sparkles, TableProperties } from 'lucide-react';
import ClassicScheduleView from '../schedule/ClassicScheduleView';
import ProScheduleView from '../schedule/pro/ProScheduleView';
import PositionsScheduleView from '../schedule/PositionsScheduleView';
import { useScheduleData } from '../schedule/useScheduleData';
import type { ScheduleView } from '../schedule/useScheduleData';
import { cn } from '../../utils/cn';
import { computeBasketTotals } from '../../utils/basketScheduleTotals';
import { LabeledControl, ModeRadio } from '../schedule/ScopeModeRadio';
import type { ScheduleViewMode } from '../../types/schedule';
// Shared with SchedulePage so the user's View / Scope choices persist across
// both the page-level schedule view and the basket Live tab.
const VIEW_KEY = 'schedule.view';
const LEGACY_VIEW_KEY = 'schedule.viewMode';
const MODE_KEY = 'schedule.mode';

function readSavedView(): ScheduleView {
  try {
    const v = localStorage.getItem(VIEW_KEY) ?? localStorage.getItem(LEGACY_VIEW_KEY);
    return v === 'pro' || v === 'positions' ? v : 'classic';
  } catch {
    return 'classic';
  }
}

function readSavedMode(): ScheduleViewMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return v === 'full' ? 'full' : 'default';
  } catch {
    return 'default';
  }
}

interface BasketScheduleViewProps {
  basketId: string;
}

/**
 * Embeds the full Schedule view inside the basket manager's Live tab, scoped
 * to a single basket. Reuses the shared `useScheduleData` hook and the
 * Classic/Pro view shells — only the strategies list and totals are filtered.
 * View-mode preference is shared with the top-level Schedule page.
 */
const BasketScheduleView: React.FC<BasketScheduleViewProps> = ({ basketId }) => {
  // Mount-only snapshots fed to the hook's lazy initializers so the very
  // first fetch sees the right (view, mode) — no redundant summary fetch
  // before a positions-view push, and no second fetch after a Platform-mode push.
  const initialViewRef = React.useRef<ScheduleView | null>(null);
  if (initialViewRef.current === null) {
    initialViewRef.current = readSavedView();
  }
  const initialModeRef = React.useRef<ScheduleViewMode | null>(null);
  if (initialModeRef.current === null) {
    initialModeRef.current = readSavedMode();
  }

  const raw = useScheduleData({
    initialView: () => initialViewRef.current as ScheduleView,
    initialViewMode: () => initialModeRef.current as ScheduleViewMode,
  });
  const {
    view: viewMode,
    setView: setViewMode,
    viewMode: scopeMode,
    setViewMode: setScopeMode,
    fetchSummary,
    refreshing,
  } = raw;

  // Persist view + scope to localStorage so they carry across surfaces
  // (basket Live tab ↔ SchedulePage).
  React.useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, viewMode); } catch { /* noop */ }
  }, [viewMode]);
  React.useEffect(() => {
    try { localStorage.setItem(MODE_KEY, scopeMode); } catch { /* noop */ }
  }, [scopeMode]);

  const filteredStrategies = React.useMemo(
    () => raw.strategies.filter((s) => s.basket_id === basketId),
    [raw.strategies, basketId],
  );

  const filteredPositions = React.useMemo(
    () => raw.positions.filter((p) => p.basket_id === basketId),
    [raw.positions, basketId],
  );

  const filteredTotals = React.useMemo(
    () => (raw.payload ? computeBasketTotals(filteredStrategies) : null),
    [filteredStrategies, raw.payload],
  );

  const data = React.useMemo(
    () => ({
      ...raw,
      strategies: filteredStrategies,
      positions: filteredPositions,
      totals: filteredTotals,
    }),
    [raw, filteredStrategies, filteredPositions, filteredTotals],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <LabeledControl label="Scope">
          <ModeRadio mode={scopeMode} onChange={setScopeMode} />
        </LabeledControl>
        <span
          aria-hidden="true"
          className="hidden sm:block h-5 w-px bg-gray-300 dark:bg-gray-600"
        />
        <LabeledControl label="View">
          <ViewModeTabs viewMode={viewMode} onChange={setViewMode} />
        </LabeledControl>
        <span
          aria-hidden="true"
          className="hidden sm:block h-6 w-px bg-gray-400 dark:bg-gray-500"
        />
        <button
          type="button"
          onClick={fetchSummary}
          disabled={refreshing}
          title="Refresh data"
          className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      {viewMode === 'positions' ? (
        <PositionsScheduleView data={data} />
      ) : viewMode === 'pro' ? (
        <ProScheduleView data={data} />
      ) : (
        <ClassicScheduleView data={data} />
      )}
    </div>
  );
};

/**
 * Three-tab segmented control for the view mode — mirrors the one on
 * SchedulePage (Classic / Pro / Positions) so the Live tab feels like the
 * same surface. All views are available to all users.
 */
const ViewModeTabs: React.FC<{
  viewMode: ScheduleView;
  onChange: (m: ScheduleView) => void;
}> = ({ viewMode, onChange }) => (
  <div
    className="inline-flex items-center bg-gray-100 dark:bg-gray-700/60 rounded-md p-0.5"
    role="tablist"
    aria-label="Schedule view mode"
  >
    <button
      type="button"
      role="tab"
      aria-selected={viewMode === 'classic'}
      onClick={() => onChange('classic')}
      className={cn(
        'px-3 py-1.5 text-xs font-medium rounded transition-colors',
        viewMode === 'classic'
          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
      )}
    >
      Classic
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={viewMode === 'pro'}
      onClick={() => onChange('pro')}
      className={cn(
        'px-3 py-1.5 text-xs font-medium rounded transition-colors inline-flex items-center gap-1',
        viewMode === 'pro'
          ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
      )}
    >
      <Sparkles className="w-3 h-3" />
      Pro
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={viewMode === 'positions'}
      onClick={() => onChange('positions')}
      className={cn(
        'px-3 py-1.5 text-xs font-medium rounded transition-colors inline-flex items-center gap-1',
        viewMode === 'positions'
          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
      )}
    >
      <TableProperties className="w-3 h-3" />
      Positions
    </button>
  </div>
);

export default BasketScheduleView;
